import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Ticket, Trash2 } from "lucide-react";
import { api, formatINR, apiError } from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const CouponsTab = () => {
  const [coupons, setCoupons] = useState(null);
  const [code, setCode] = useState("");
  const [type, setType] = useState("percent");
  const [value, setValue] = useState("");
  const [expiry, setExpiry] = useState("");
  const [creating, setCreating] = useState(false);

  const load = useCallback(() => {
    api
      .get("/admin/coupons")
      .then((r) => setCoupons(r.data.coupons))
      .catch((e) => {
        toast.error(apiError(e, "Could not load coupons"));
        setCoupons([]);
      });
  }, []);

  useEffect(load, [load]);

  const create = async (e) => {
    e.preventDefault();
    if (!code.trim()) return toast.error("Enter a coupon code");
    if (!value || Number(value) <= 0) return toast.error("Enter a valid discount value");
    setCreating(true);
    try {
      await api.post("/admin/coupons", {
        code: code.trim().toUpperCase(),
        type,
        value: Number(value),
        expires_at: expiry ? `${expiry}T23:59:59` : null,
      });
      toast.success(`Coupon ${code.trim().toUpperCase()} created`);
      setCode("");
      setValue("");
      load();
    } catch (err) {
      toast.error(apiError(err, "Could not create coupon"));
    } finally {
      setCreating(false);
    }
  };

  const toggleActive = async (coupon, active) => {
    const prev = coupons;
    setCoupons((list) => list.map((c) => (c.id === coupon.id ? { ...c, active } : c)));
    try {
      await api.patch(`/admin/coupons/${coupon.id}/active`, { active });
    } catch (err) {
      setCoupons(prev);
      toast.error(apiError(err, "Could not update coupon"));
    }
  };

  const remove = async (coupon) => {
    try {
      await api.delete(`/admin/coupons/${coupon.id}`);
      toast.success(`Coupon ${coupon.code} deleted`);
      load();
    } catch (err) {
      toast.error(apiError(err, "Could not delete coupon"));
    }
  };

  return (
    <div className="mx-auto mt-10 max-w-3xl" data-testid="coupons-tab">
      <form
        onSubmit={create}
        className="border border-cream/10 bg-espresso-surface p-6 sm:p-8"
        data-testid="coupon-create-form"
      >
        <div className="flex items-center gap-3">
          <Ticket size={18} strokeWidth={1.5} className="text-gold" />
          <h2 className="font-serif text-2xl text-cream">Create a Coupon</h2>
        </div>
        <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-4">
          <input
            data-testid="coupon-code-input"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 20))}
            placeholder="DIWALI20"
            className="border border-cream/15 bg-transparent px-4 py-3 font-mono text-sm uppercase tracking-[0.15em] text-cream outline-none transition-colors placeholder:text-cream/25 focus:border-gold sm:col-span-2"
          />
          <Select value={type} onValueChange={setType}>
            <SelectTrigger
              data-testid="coupon-type-select"
              className="rounded-none border-cream/15 bg-transparent font-mono text-[10px] uppercase tracking-[0.15em] text-cream"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="rounded-none border-gold/30 bg-espresso-surface text-cream">
              <SelectItem value="percent" data-testid="coupon-type-percent">Percent %</SelectItem>
              <SelectItem value="fixed" data-testid="coupon-type-fixed">Fixed ₹</SelectItem>
            </SelectContent>
          </Select>
          <input
            data-testid="coupon-value-input"
            type="number"
            min="1"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={type === "percent" ? "e.g. 20" : "e.g. 500"}
            className="border border-cream/15 bg-transparent px-4 py-3 text-sm text-cream outline-none transition-colors placeholder:text-cream/25 focus:border-gold"
          />
          <input
            data-testid="coupon-expiry-input"
            type="date"
            value={expiry}
            min={new Date().toISOString().slice(0, 10)}
            onChange={(e) => setExpiry(e.target.value)}
            title="Expiry date (optional)"
            className="border border-cream/15 bg-transparent px-4 py-3 text-sm text-cream outline-none transition-colors [color-scheme:dark] focus:border-gold"
          />
        </div>
        <p className="mt-2 text-xs text-cream/35">
          Expiry is optional — the code stops working at the end of that date.
        </p>
        <button
          data-testid="coupon-create-button"
          type="submit"
          disabled={creating}
          className="mt-4 inline-flex items-center gap-2 bg-gold px-8 py-3 font-mono text-[10px] uppercase tracking-[0.25em] text-espresso transition-all duration-300 hover:bg-gold-accent active:scale-[0.98] disabled:opacity-50"
        >
          <Plus size={14} /> {creating ? "Creating…" : "Create Coupon"}
        </button>
      </form>

      <div className="mt-6 border border-cream/10">
        {coupons === null ? (
          <p className="px-6 py-10 text-center font-mono text-[10px] uppercase tracking-[0.25em] text-cream/30">
            Loading…
          </p>
        ) : coupons.length === 0 ? (
          <p
            data-testid="coupons-empty-state"
            className="px-6 py-10 text-center font-mono text-[10px] uppercase tracking-[0.25em] text-cream/30"
          >
            No coupons yet — create your first above
          </p>
        ) : (
          <ul className="divide-y divide-cream/8" data-testid="coupons-list">
            {coupons.map((c) => (
              <li
                key={c.id}
                data-testid={`coupon-row-${c.code}`}
                className={`flex items-center justify-between gap-4 px-6 py-4 ${!c.active ? "opacity-50" : ""}`}
              >
                <div>
                  <p className="font-mono text-sm tracking-[0.2em] text-cream">{c.code}</p>
                  <p className="mt-1 text-xs text-cream/50">
                    {c.type === "percent" ? `${c.value}% off the order` : `${formatINR(c.value)} off the order`}
                    {c.expires_at && (
                      <span
                        data-testid={`coupon-expiry-${c.code}`}
                        className={c.expired ? "text-red-400" : "text-gold-muted"}
                      >
                        {" · "}
                        {c.expired
                          ? "Expired"
                          : `Ends ${new Date(c.expires_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}`}
                      </span>
                    )}
                  </p>
                </div>
                <div className="flex items-center gap-4">
                  <Switch
                    data-testid={`coupon-active-toggle-${c.code}`}
                    checked={c.active}
                    onCheckedChange={(v) => toggleActive(c, v)}
                  />
                  <button
                    data-testid={`coupon-delete-${c.code}`}
                    onClick={() => remove(c)}
                    className="flex h-8 w-8 items-center justify-center border border-cream/15 text-cream/50 transition-colors hover:border-red-400 hover:text-red-400"
                    aria-label={`Delete ${c.code}`}
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default CouponsTab;
